// src/components/profiles/Member/components/posts/pray/PrayerGridItem.tsx
"use client";

import React, { useMemo, useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAppDispatch } from "../../../../../../redux/hooks";

import {
  deletePrayerBySlug,
  fetchPrayerBySlug,
} from "../../../../../../redux/ugc/prayerSlice";

import { Edit3, Edit2, Edit, Trash2, PlusCircle } from "lucide-react";

import type { PrayerDTO } from "../../../../../../types/ugc/prayerTypes";
import { makeMediaUrl } from "../../../../../../utils/navigation/contentNavigation";
import { UGC_ENDPOINTS } from "../../../../../../services/ugc/endpoint/endpoints";
import { ActionMenu, Modal } from "../../../../../common";

import MediaConversionPanel from "../../../../../mediaConversion/MediaConversionPanel/MediaConversionPanel";
import { useMediaConversionJobs } from "../../../../../../hooks/mediaConversion/useMediaConversionJobs";

import PrivateCdnImage from "@/asset-delivery/components/PrivateCdnImage";
import { toCleanCdnUrl } from "@/asset-delivery/utils/toCleanCdnUrl";

import "./PrayerGridItem.css";

type Props = {
  prayer: PrayerDTO;
};



// Normalize backend job_target into frontend MediaJobTarget shape
function normalizeMediaJobTarget(raw: any): any | null {
  if (!raw) return null;

  // 1) If backend already gives ct info (no "by"), use ct (most reliable)
  const ctId = raw.contentTypeId ?? raw.content_type_id ?? raw.content_type;
  const objId = raw.objectId ?? raw.object_id;

  if (typeof ctId === "number" && typeof objId === "number") {
    return {
      by: "ct",
      contentTypeId: ctId,
      objectId: objId,
    };
  }

  // 2) If backend sends content_type_model like "posts.prayerresponse"
  const ctm = raw.content_type_model ?? raw.contentTypeModel;
  if (typeof ctm === "string" && (raw.object_id || raw.objectId)) {
    const [appLabel, model] = ctm.split(".");
    if (appLabel && model) {
      return {
        by: "model",
        appLabel,
        model, // note: keep as provided
        objectId: raw.objectId ?? raw.object_id,
      };
    }
  }

  // 3) If backend uses explicit "by" (older variants)
  const by = raw.by;

  if (by === "content_type") {
    return {
      by: "ct",
      contentTypeId: raw.contentTypeId ?? raw.content_type_id ?? raw.content_type,
      objectId: raw.objectId ?? raw.object_id,
    };
  }

  if (by === "model") {
    return {
      by: "model",
      appLabel: raw.appLabel ?? raw.app_label,
      model: raw.model,
      objectId: raw.objectId ?? raw.object_id,
    };
  }

  if (by === "slug") {
    return {
      by: "slug",
      appLabel: raw.appLabel ?? raw.app_label,
      model: raw.model,
      slug: raw.slug,
    };
  }

  return null;
}


const buildPrivateCdnTarget = (p: PrayerDTO) => ({
  app_label: "posts",
  model: "prayer",
  object_id: (p as any)?.id,
});

export default function PrayerGridItem({ prayer }: Props) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const didRefetchMainRef = useRef(false);
  const didRefetchResponseRef = useRef(false);

  const canManage = prayer.owner?.is_me === true;
  const hasResponse = Boolean(prayer.response);

  const statusKey = useMemo(() => {
    const s = (prayer as any)?.status as string | undefined;
    if (!hasResponse || s === "waiting") return "in_hope";
    if (s === "answered") return "granted";
    if (s === "not_answered") return "withheld";
    return "in_hope";
  }, [hasResponse, (prayer as any)?.status]);

  const statusLabel = useMemo(() => {
    if (statusKey === "granted") return "Granted";
    if (statusKey === "withheld") return "Withheld";
    return "In Hope";
  }, [statusKey]);

  // -------------------------------------------------
  // Conversion state (main prayer media)
  // FIX: image-only prayers must NOT be treated as video
  // -------------------------------------------------
  const mediaType = (prayer as any)?.media_type ?? null;

  // job target may be attached by gate_media_payload (same as response)
  const prayerJobTarget =
    (prayer as any)?.job_target ||
    (prayer as any)?.jobTarget ||
    (prayer as any)?.job_target_video ||
    null;

  // Detect "video intent" even when backend hides `video` during conversion
  const hasPrayerVideoHint =
    Boolean((prayer as any)?.video_key) ||
    Boolean((prayer as any)?.video_signed_url) ||
    Boolean((prayer as any)?.video) ||
    Boolean(prayerJobTarget); // ✅ key point (works while video is gated)

  // Prefer explicit media_type if present, else infer
  const isVideoPrayer = mediaType ? mediaType === "video" : hasPrayerVideoHint;

  // Converting only when backend says not converted AND we have a reliable hint
  const isConverting =
    isVideoPrayer &&
    (prayer as any)?.is_converted === false &&
    Boolean(prayerJobTarget || hasPrayerVideoHint);

  // Stable conversion target
  const mediaTarget = useMemo(
    () => ({
      by: "slug" as const,
      appLabel: "posts",
      model: "prayer",
      slug: prayer.slug,
    }),
    [prayer.slug]
  );

  // Poll conversion jobs only while converting
  const { jobs: conversionJobs } = useMediaConversionJobs(mediaTarget, {
    enabled: isConverting, // ✅ enough now (we already gated isConverting)
    fieldName: "video",
  });

  // Active conversion UI gate
  const hasActiveConversion =
    isConverting ||
    conversionJobs.some(
      (j) => j.status === "queued" || j.status === "processing"
    );

  // Block navigation while converting
  const href = useMemo(() => {
    if (hasActiveConversion) return "#";
    return makeMediaUrl(prayer.slug, {
      endpoint: UGC_ENDPOINTS.prayers.root,
      scope: "flat",
      aspect: "auto",
      usePoster: true,
    });
  }, [prayer.slug, hasActiveConversion]);

  // Reset refetch marker on item change -------------------------------------------------
  useEffect(() => {
    didRefetchMainRef.current = false;
    didRefetchResponseRef.current = false;
  }, [prayer.slug]);

  // Refetch prayer when conversion finishes ---------------------------------------------
  useEffect(() => {
    if (
      isVideoPrayer &&
      !didRefetchMainRef.current &&
      conversionJobs.length > 0 &&
      conversionJobs.every((j) => j.status === "done" || j.status === "failed")
    ) {
      didRefetchMainRef.current = true;
      dispatch(fetchPrayerBySlug(prayer.slug));
    }
  }, [conversionJobs, isVideoPrayer, dispatch, prayer.slug]);

  // -------------------------------------------------
  // Conversion state (response media)
  // IMPORTANT:
  // - response.video may be null while converting (serializer gates it)
  // - so DO NOT infer conversion from response.video
  // - infer from (response.is_converted === false) AND presence of job_target (preferred)
  // -------------------------------------------------
  const responseObj = (prayer as any)?.response || null;

  // job target may be attached by gate_media_payload (names may differ)
  const responseJobTarget =
    responseObj?.job_target ||
    responseObj?.jobTarget ||
    responseObj?.job_target_video || // just in case you named it differently
    null;

  // If job_target exists and is_converted is false => converting
  const isResponseConverting =
    Boolean(responseObj) &&
    responseObj?.is_converted === false &&
    Boolean(responseJobTarget);

  // Field name must match backend conversion config key: "video"
  const responseFieldName =
    responseJobTarget?.field_name ||
    responseJobTarget?.fieldName ||
    "video";

  // Use job_target when available (best), else fallback to PrayerResponse model target
  const responseMediaTarget = useMemo(() => {
    if (!responseObj) return null;

    // Normalize backend target (preferred)
    const normalized = normalizeMediaJobTarget(responseJobTarget);
    if (normalized) return normalized;

    // Fallback (still ct is safest if we can build it)
    // If you have no ctId here, keep model fallback
    return {
      by: "model" as const,
      appLabel: "posts",
      model: "prayerresponse",
      objectId: responseObj.id,
    };
  }, [responseObj, responseJobTarget]);

  // Main prayer target (for warming main media)
  const prayerPrivateTarget = useMemo(
    () => ({
      app_label: "posts",
      model: "prayer",
      object_id: (prayer as any)?.id,
    }),
    [(prayer as any)?.id]
  );

  // Response target (for warming response media)
  const responsePrivateTarget = useMemo(() => {
    if (!responseObj?.id) return null;
    return {
      app_label: "posts",
      model: "prayerresponse",
      object_id: responseObj.id,
    };
  }, [responseObj?.id]);

  // Poll response conversion jobs only while converting
  const { jobs: responseConversionJobs } = useMediaConversionJobs(
    responseMediaTarget,
    { enabled: isResponseConverting, fieldName: responseFieldName } // ✅ use computed fieldName
  );

  const hasActiveResponseConversion =
    isResponseConverting ||
    responseConversionJobs.some(
      (j) => j.status === "queued" || j.status === "processing"
    );


  // Refetch prayer when response conversion finishes
  useEffect(() => {
    if (
      isResponseConverting &&
      responseConversionJobs.length > 0 &&
      responseConversionJobs.every((j) => j.status === "done" || j.status === "failed") &&
      !didRefetchResponseRef.current
    ) {
      didRefetchResponseRef.current = true;
      dispatch(fetchPrayerBySlug(prayer.slug));
    }
  }, [isResponseConverting, responseConversionJobs, dispatch, prayer.slug]);


  // -----------------------------
  // Response links
  // -----------------------------
  const responseCreateHref = useMemo(() => {
    const current =
      typeof window !== "undefined" ? new URL(window.location.href) : null;

    const params = new URLSearchParams(current?.search || "");
    params.set("postTab", "prayers");
    params.set("focusPrayer", prayer.slug);

    const path = current
      ? `${current.pathname}?${params.toString()}${current.hash || ""}`
      : `/?postTab=prayers&focusPrayer=${encodeURIComponent(prayer.slug)}`;

    return `/prayers/${prayer.slug}/response/create?returnTo=${encodeURIComponent(path)}`;
  }, [prayer.slug]);

  const responseEditHref = useMemo(() => {
    const current =
      typeof window !== "undefined" ? new URL(window.location.href) : null;

    const params = new URLSearchParams(current?.search || "");
    params.set("postTab", "prayers");
    params.set("focusPrayer", prayer.slug);

    const path = current
      ? `${current.pathname}?${params.toString()}${current.hash || ""}`
      : `/?postTab=prayers&focusPrayer=${encodeURIComponent(prayer.slug)}`;

    return `/prayers/${prayer.slug}/response/edit?returnTo=${encodeURIComponent(path)}`;
  }, [prayer.slug]);

  // -----------------------------
  // CDN sources
  // -----------------------------
  const cleanImage = useMemo(
    () => toCleanCdnUrl((prayer as any)?.image),
    [(prayer as any)?.image]
  );

  const cleanThumb = useMemo(
    () => toCleanCdnUrl((prayer as any)?.thumbnail),
    [(prayer as any)?.thumbnail]
  );

  // Always use CDN url (no S3 signed urls)
  const responseImageSrc = useMemo(() => {
    return toCleanCdnUrl((responseObj as any)?.image) || null;
  }, [responseObj]);

  const responseThumbSrc = useMemo(() => {
    return toCleanCdnUrl((responseObj as any)?.thumbnail) || null;
  }, [responseObj]);


  // Prefer thumbnail for video cards (same policy as Moment)
  const preferredMainSrc = useMemo(() => {
    if (isVideoPrayer) return cleanThumb || cleanImage || null;
    return cleanImage || cleanThumb || null;
  }, [isVideoPrayer, cleanThumb, cleanImage]);

  const privateTarget = useMemo(() => buildPrivateCdnTarget(prayer), [prayer]);

  // Warm main prayer image/thumbnail
  const mainWarmKind = useMemo(() => {
    if (isVideoPrayer) return cleanThumb ? "thumbnail" : "image";
    return cleanThumb ? "thumbnail" : "image";
  }, [isVideoPrayer, cleanThumb]);

  const mainWarmFieldName = mainWarmKind;

  // -----------------------------
  // Delete modal
  // -----------------------------
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await dispatch(deletePrayerBySlug(prayer.slug)).unwrap();
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const actionOptions = useMemo(() => {
    if (!canManage) return [];

    const current =
      typeof window !== "undefined" ? new URL(window.location.href) : null;

    const params = new URLSearchParams(current?.search || "");
    params.set("postTab", "prayers");
    params.set("focusPrayer", prayer.slug);

    const path = current
      ? `${current.pathname}?${params.toString()}${current.hash || ""}`
      : `/?postTab=prayers&focusPrayer=${encodeURIComponent(prayer.slug)}`;

    const prayerEditUrl = `/prayers/${prayer.slug}/edit?returnTo=${encodeURIComponent(path)}`;
    const responseUrl = hasResponse
      ? `/prayers/${prayer.slug}/response/edit?returnTo=${encodeURIComponent(path)}`
      : `/prayers/${prayer.slug}/response/create?returnTo=${encodeURIComponent(path)}`;

    return [
      {
        label: "Edit Prayer",
        icon: <Edit3 size={16} />,
        onClick: () => router.push(prayerEditUrl),
      },
      {
        label: hasResponse ? "Edit Response" : "Add Response",
        icon: hasResponse ? <Edit size={16} /> : <PlusCircle size={16} />,
        onClick: () => router.push(responseUrl),
      },
      {
        label: "Delete",
        icon: <Trash2 size={16} />,
        danger: true,
        onClick: () => setShowDeleteModal(true),
      },
    ];
  }, [canManage, hasResponse, prayer.slug, router]);

  // -------------------------------------------------
  // Converting render (panel only, no clickable card)
  // -------------------------------------------------
  if (hasActiveConversion) {
    return (
      <>
        <div className="prayer-grid-item-wrapper" data-prayer-slug={prayer.slug}>
          <MediaConversionPanel
            target={mediaTarget}
            fieldName="video"
            compact
            title="Processing prayer video"
            density="mini"
          />
        </div>

        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Prayer"
          message={
            <>
              <p>Are you sure you want to delete this prayer?</p>
              <p>
                <strong>This action cannot be undone.</strong>
              </p>
            </>
          }
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="danger"
          isLoading={isDeleting}
          onConfirm={handleDelete}
        />
      </>
    );
  }

  // -----------------------------
  // Ready render
  // -----------------------------
  return (
    <>
      <div className="prayer-grid-item-wrapper" data-prayer-slug={prayer.slug}>
        {/* Owner menu */}
        {canManage && (
          <div
            className="prayer-grid-item__menu"
            onClick={(e) => e.stopPropagation()}
          >
            <ActionMenu
              options={actionOptions}
              position="bottom-right"
              color="secondary"
              iconSize={28}
              menuWidth="150px"
            />
          </div>
        )}

        <div
          className={`prayer-grid-item ${hasResponse ? "prayer-grid-item--answered" : "prayer-grid-item--waiting"
            }`}
        >
          {/* --------------------------- LEFT - Prayer --------------------------- */}
          <Link href={href} prefetch={false} className="prayer-col" aria-label="Open prayer">
            {preferredMainSrc ? (
              <PrivateCdnImage
                src={preferredMainSrc}
                placeholderSrc={null}
                alt=""
                className="prayer-img-wrapper"
                imgClassName="prayer-img"
                target={privateTarget}
                kind={mainWarmKind as any}
                fieldName={mainWarmFieldName as any}
                rootMargin="800px"
                threshold={0.01}
                failOpen
              />
            ) : (
              <div className="prayer-media-placeholder" />
            )}
          </Link>

          {/* --------------------------- MIDDLE - Status --------------------------- */}
          {hasResponse && !hasActiveResponseConversion ? (
            <Link
              href={href}
              prefetch={false}
              className={`prayer-status prayer-status--${statusKey} prayer-status--link`}
              aria-label={`Prayer status: ${statusLabel}. Open prayer`}
            >
              <div className="prayer-status__text">{statusLabel}</div>
            </Link>
          ) : (
            <div
              className={`prayer-status prayer-status--${statusKey}`}
              aria-label={`Prayer status: ${statusLabel}`}
            >
              <div className="prayer-status__text">{statusLabel}</div>
            </div>
          )}

          {/* --------------------------- RIGHT - Response --------------------------- */}
          <Link
            href={
              hasActiveResponseConversion
                ? "#"
                : !hasResponse
                  ? responseCreateHref
                  : href
            }
            prefetch={false}
            className="prayer-col prayer-col--response"
            aria-label={
              hasActiveResponseConversion
                ? "Response is processing"
                : !hasResponse
                  ? "Add prayer response"
                  : "Open prayer"
            }
            onClick={(e) => {
              if (hasActiveResponseConversion) e.preventDefault();
            }}
          >
            {hasActiveResponseConversion ? (
              <MediaConversionPanel
                target={responseMediaTarget}
                fieldName={responseFieldName}
                compact
                title="Processing response video"
                density="micro"
              />
            ) : hasResponse && (responseImageSrc || responseThumbSrc) ? (
              <PrivateCdnImage
                src={responseThumbSrc || responseImageSrc}
                placeholderSrc={null}
                alt=""
                className="prayer-img-wrapper"
                imgClassName="prayer-img"
                target={responsePrivateTarget ?? prayerPrivateTarget}
                kind={(responseThumbSrc ? "thumbnail" : "image") as any}
                fieldName={(responseThumbSrc ? "thumbnail" : "image") as any}
                rootMargin="800px"
                threshold={0.01}
                failOpen
              />
            ) : (
              <div className="prayer-response-placeholder">
                <div className="prayer-response-placeholder__waves" aria-hidden="true">
                  <span className="prayer-response-placeholder__wave prayer-response-placeholder__wave--1" />
                  <span className="prayer-response-placeholder__wave prayer-response-placeholder__wave--2" />
                </div>

                <div className="prayer-response-placeholder__iconWrap">
                  <PlusCircle size={32} />
                </div>
              </div>
            )}
          </Link>

        </div>
      </div>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Prayer"
        message={
          <>
            <p>Are you sure you want to delete this prayer?</p>
            <p>
              <strong>This action cannot be undone.</strong>
            </p>
          </>
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={isDeleting}
        onConfirm={handleDelete}
      />
    </>
  );
}